/**
 * What crm-api's `invoices#bulk_delete` actually did, in words.
 *
 * The toast used to say "Successfully deleted N" from the number of ids SENT,
 * even when crm-api deleted fewer. The sentence now comes from the response:
 * the ids it reports deleted, and how many it could not delete.
 */
export interface BulkDeleteResponse {
  data?: {
    deleted_count?: number
    deleted_ids?: number[]
    failed_count?: number
  }
}

export interface BulkDeleteReport {
  deletedIds: number[]
  title: string
  description: string
  /** "destructive" only when NOTHING was deleted. */
  variant?: "destructive"
}

export function describeBulkDelete(
  response: BulkDeleteResponse | undefined,
  requestedIds: number[],
  noun: "invoice" | "quote",
): BulkDeleteReport {
  const data = response?.data ?? {}
  // An older crm-api answers without deleted_ids: trust the request, as before.
  const deletedIds = Array.isArray(data.deleted_ids) ? data.deleted_ids : requestedIds
  const failed = data.failed_count ?? 0
  const n = deletedIds.length
  const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`

  const parts: string[] = []
  if (n > 0) parts.push(`Deleted ${plural(n, noun)}.`)
  if (failed > 0) parts.push(`${plural(failed, noun)} could not be deleted.`)

  if (n === 0) {
    return { deletedIds, title: "Nothing deleted", description: parts.join(" ") || `No ${noun}s were deleted.`, variant: "destructive" }
  }
  return { deletedIds, title: failed > 0 ? "Partly deleted" : "Success", description: parts.join(" ") }
}
